import { isStandalone } from '@digital-retail/store-manager';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import apiConfig from '../config/api/apiConfig';
import { constants } from '../config/constants';
import { storeMap } from '../config/storeMap';
import ProductLandingContainer from '../containers/ProductLanding/ProductLandingContainer';
import { getHostUrl } from '../containers/SearchResults/utils';
import { withApplicationContext } from '../utils/ApplicationContext';
import { CartContextProvider } from '../utils/CartContext';
import { CookieStorage } from '../utils/CookieStorage';
import { navigate, redirect } from '../utils/RouteHandler';
import { CollectionSEO } from '../utils/SEO/CollectionSEO';
import { WebStorage } from '../utils/WebStorage';
import { WithUrl } from '../utils/WithUrl';
import { addProductListEvent } from '../utils/dataLayerHelper';
import { isWindow } from '../utils/deviceChecker';
import { useOnScroll } from '../utils/hooks/UseOnScroll';
import { httpService } from '../utils/httpService';
import { replaceImageDimensions } from '../utils/imageUrlHelper';
import { isKiosk } from '../utils/kisokHelper';
import { handleScroll } from '../utils/paginationUtils';
import { cleanText } from '../utils/textHelper';
import { getPaginatedUrl as getPaginatedUrlUtil, getRequestUrl } from '../utils/urlHelper';
import Error from './_error';

const updateLocation = () => {
  WebStorage.setItem({
    name: constants.SESSION_STORAGE_SCROLL,
    value: `${window.scrollX},${window.scrollY}`,
  });
};

const Collection = (props) => {
  const { statusCode } = props;
  // eslint-disable-next-line react/prop-types
  const { appCtx, layout, ...initialData } = props; // InitialData should be empty

  const { regionCode, host, store } = appCtx;

  const data = useRef();

  if (Object.entries(initialData).length !== 0) {
    data.current = initialData;
  }

  const {
    searchTerm,
    pagination,
    facets,
    promotedFilter,
    baseFacetsURL,
    appliedFacets,
    autoCorrections,
    availabilityFacets,
    results,
    sortOptions,
    currentLocationId,
    site,
    metadata,
    categoryBannerHtml,
    leftPromotionContent,
    canonicalUrl,
    currentUrl,
    collectionId,
  } = data.current || {};

  useEffect(() => {
    appCtx.attachResizeListener();
  }, [data.current]);

  useEffect(() => {
    handleScroll(appCtx);
    if (results) {
      addProductListEvent(results, 'collection', store, regionCode);
    }
  }, [results]);

  useOnScroll({
    handler: updateLocation,
  });

  const getPaginatedUrl = (paginationIndex) => {
    return getPaginatedUrlUtil(currentUrl, paginationIndex, store, regionCode);
  };

  if (statusCode === 500) {
    return <Error />;
  }

  return (
    <>
      <CollectionSEO
        SiteProps={{
          regionCode,
          metadata,
          canonicalUrl,
          host,
          store,
          facets,
        }}
      />
      <CartContextProvider>
        <ProductLandingContainer
          pagination={{
            data: pagination,
            getPaginatedUrl,
          }}
          layout={layout}
          products={results}
          promotedFilter={promotedFilter}
          autoCorrections={autoCorrections}
          facets={facets}
          baseFacetsURL={baseFacetsURL}
          appliedFacets={appliedFacets}
          searchTerm={searchTerm}
          availabilityFacets={availabilityFacets}
          sortOptions={sortOptions}
          site={site}
          currentLocationId={currentLocationId}
          brandDetails={metadata}
          categoryBannerHtml={categoryBannerHtml}
          leftPromotionContent={leftPromotionContent}
          pageName="collection"
          collectionId={collectionId}
          canonicalUrl={canonicalUrl}
        />
      </CartContextProvider>
    </>
  );
};

const shouldRedirect = (responseType) => {
  return responseType === constants.SEARCH_REDIRECT_STATUS;
};

const redirectCollection = (response, url, redirectCode = constants.HTTP_RES_CODE.TEMP_REDIRECT) => {
  if (isWindow()) {
    redirect(url, true);
    return;
  }

  if (response) {
    response.writeHead(redirectCode, { Location: url });
    response.end();
  }
};

const redirectNoSearchResult = (response, site, regionCode, termo, store) => {
  if (isWindow()) {
    navigate({
      url: '/noResult',
      params: {
        Ntt: termo,
      },
      site,
    });
    return;
  }

  if (response) {
    const url = `/${storeMap[store] || 'falabella'}-${regionCode}/noResult?Ntt=${termo}`;
    response.writeHead(constants.HTTP_RES_CODE.TEMP_REDIRECT, { Location: url });
    response.end();
  }
};

const getCanonicalUrl = (region, collectionId, query, store) => {
  const { page } = query;
  const cleanName = cleanText(collectionId).toLowerCase();
  const storeValidated = store && storeMap[store.toLowerCase()] ? store : null;
  const url = WithUrl(
    isStandalone({ store }) ? 'SA_COLLECTION_PAGE' : 'COLLECTION_PAGE',
    region,
    {
      custom: {
        collectionId: cleanName,
      },
    },
    storeValidated
  );

  if (page > 1) {
    return `${url}?page=${page}`;
  }
  return url;
};

const isCanonicalUrl = (currentUrl, collectionName) => {
  const cleanName = cleanText(collectionName).toLowerCase();
  const urlPath = currentUrl.match('(/{0,1}[^?#]*)')[0].split('/');
  const currentName = urlPath[urlPath.length - 1];
  return currentName === cleanName;
};

const shouldSEORedirect = (appCtx, currentUrl, title) => {
  const isRedirect301EnabledForCollection = appCtx?.siteConfig?.toggles?.isRedirect301EnabledForCollection ?? false;

  return isRedirect301EnabledForCollection && !isCanonicalUrl(currentUrl, title);
};

const getUrlToRedirect = (currentUrl, canonicalUrl) => {
  const queryStrings = currentUrl.split('?')[1];
  const params = queryStrings ? `?${queryStrings}` : '';
  return `${encodeURI(canonicalUrl)}${params}`;
};

Collection.getInitialProps = async ({ config }, { query, req, res, asPath }, appCtx) => {
  const { site, id, name: _name, ...rest } = query;
  const { regionCode: siteId, store, latLong } = appCtx;

  const searchTerm = encodeURIComponent(query.name);

  rest.collectionId = id;
  rest.pgid = appCtx.priceGroupId;
  rest.pid = appCtx.politicalId;
  rest.zones = appCtx.zones;
  if (latLong) {
    rest.latLong = encodeURIComponent(JSON.stringify(latLong));
  }
  const params = {};
  Object.keys(rest).forEach((key) => {
    const value = rest[key];
    params[encodeURIComponent(key)] = encodeURIComponent(value);
  });

  if (store) {
    params[encodeURIComponent('store')] = encodeURIComponent(store);
  }

  if (isKiosk(appCtx)) {
    params.channel = 'KIOSK';
  }

  const cookies = CookieStorage.parseCookies(appCtx.ssrCookies);

  if (appCtx.isTottusSA) {
    if (cookies.USER_ID) {
      params.userId = cookies.USER_ID;
    }
    params.standardZones = appCtx.standardZones;
    params.expressZones = appCtx.expressZones;
  }

  const collectionUrl = apiConfig.getCollectionUrl({
    base: getHostUrl({ config, store, regionCode: siteId }),
    siteId,
    rest: params,
  });

  const currentLocationId = appCtx.politicalId;

  const headersForProductSearch = isWindow() ? {} : { cookie: appCtx.ssrCookies };
  const response = await httpService(appCtx).get(
    collectionUrl,
    {
      includeTracing: true,
      reqId: req && req.id,
      headers: {
        'Content-Type': 'application/json',
        ...appCtx.tracingHeaders,
        ...headersForProductSearch,
      },
    },
    'json'
  );

  const { data, error: collectionApiErrors, statusCode } = response;

  if (collectionApiErrors && statusCode !== 409) {
    res.statusCode = 500;
    return {
      statusCode: 500,
    };
  }

  if (shouldRedirect(data.responseType)) {
    if (!isWindow() && res && !(res.finished || res.headersSent)) {
      res.setHeader('Cache-Control', 'no-cache');
    }
    redirectCollection(res, data.data.altUrl);
    return undefined;
  }

  const metadata = data?.data?.metadata ?? {};
  const canonicalUrl = getCanonicalUrl(siteId, metadata.id, rest, store);

  const currentUrl = isWindow() ? asPath : getRequestUrl(req.url, appCtx.tenant);

  const {
    data: {
      data: {
        layout,
        results,
        facets,
        appliedFacets,
        baseFacetsURL,
        availabilityFacets,
        promotedFilter,
        sortOptions,
        pagination,
        autoCorrections,
        relatedKeywordBanner,
        leftPromotionContent,
      },
    },
  } = response;

  if (results && results.length === 0) {
    if (!isWindow() && res && !(res.finished || res.headersSent)) {
      res.setHeader('Cache-Control', 'no-cache');
    }
    redirectNoSearchResult(res, site, appCtx.regionCode, '', store);
    return undefined;
  }

  if (!metadata.canonical && shouldSEORedirect(appCtx, currentUrl, metadata.id)) {
    if (!isWindow() && res && !(res.finished || res.headersSent)) {
      res.setHeader('Cache-Control', 'no-cache');
    }
    const urlToRedirect = getUrlToRedirect(currentUrl, canonicalUrl);

    redirectCollection(res, urlToRedirect, constants.HTTP_RES_CODE.PERM_REDIRECT);
    return undefined;
  }

  if (!isWindow() && res && !(res.finished || res.headersSent)) {
    res.setHeader('Cache-Tag', 'catalyst-SLP-v1');
    const cacheKeyMaxAge = 365 * 24 * 60 * 60;
    res.setHeader(
      'Set-Cookie',
      `CACHE_KEY=${appCtx.buildId}; max-age=${cacheKeyMaxAge}; Path=/${appCtx.tenant}/search`
    );
  }

  let categoryBannerHtml = '';
  if (relatedKeywordBanner) {
    if (config.FETCH_BANNERS_IN_SERVER_SIDE === false && relatedKeywordBanner.includes('assets.contentstack')) {
      categoryBannerHtml = relatedKeywordBanner;
    } else {
      const { data: htmlData, error } = await httpService().get(relatedKeywordBanner, {
        includeTracing: false,
        asText: true,
      });

      if (!error) {
        categoryBannerHtml = replaceImageDimensions(htmlData, 500);
      }
    }
  }
  return {
    results,
    layout,
    facets,
    baseFacetsURL,
    appliedFacets,
    searchTerm,
    promotedFilter,
    pagination,
    sortOptions,
    autoCorrections,
    availabilityFacets,
    currentLocationId,
    site: encodeURIComponent(query.site),
    metadata,
    categoryBannerHtml,
    leftPromotionContent,
    canonicalUrl,
    currentUrl,
    collectionId: id,
  };
};

Collection.propTypes = {
  layout: PropTypes.string.isRequired,
  searchTerm: PropTypes.string.isRequired,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  facets: PropTypes.arrayOf(PropTypes.object).isRequired,
  appliedFacets: PropTypes.array.isRequired,
  pagination: PropTypes.object.isRequired,
  site: PropTypes.string.isRequired,
  appCtx: PropTypes.object.isRequired,
  sortOptions: PropTypes.array.isRequired,
  autoCorrections: PropTypes.array.isRequired,
  availabilityFacets: PropTypes.array.isRequired,
  baseFacetsURL: PropTypes.string.isRequired,
  currentLocationId: PropTypes.string.isRequired,
  categoryBannerHtml: PropTypes.string.isRequired,
  statusCode: PropTypes.number.isRequired,
  canonicalUrl: PropTypes.string.isRequired,
};

export { Collection };

export default withApplicationContext(Collection);
